/**
 * BookingPage — the operator-facing Bookings list (BON-130).
 *
 * One responsive page that switches layout by viewport (useViewport):
 *   - phone  (<768)     → single-column card list
 *   - tablet (768–1023) → 2-column card grid
 *   - desktop (≥1024)   → data table
 *
 * Status filter + search + sort drive the v2 list query params
 * (status | q | sort | dir); results are paged (pageSize 20) with the shared
 * PaginationFooter; the KPI strip pulls the week-scoped stats independently.
 * Region scoping is applied server-side. The "New booking" CTA (header +
 * mobile FAB) opens the CSR manual-booking modal (BON-142); a successful
 * create refreshes the list + KPI strip in place.
 */
import { useSearchParams } from 'react-router-dom';

import { useFocusHeadingOnNavigate } from '../../hooks/useFocusHeadingOnNavigate';
import { useViewport } from '../../hooks/useViewport';
import { useAppMode } from '../../shared/nav/use-app-mode';

import { BookingsTabBody } from './components/BookingsTabBody';
import { NewBookingButton } from './components/NewBookingButton';
import { type BookingTab, Tabs } from './components/Tabs';
import { ReservationsPanel } from './ReservationsPanel';
import { useCreateBookingLauncher } from './shared/CreateBookingLauncher';
import { useBookingsList } from './useBookingsListState';
import { useReservationsList } from './useReservationsList';

export default function BookingPage() {
  const { isPhone, isDesktop } = useViewport();
  const isTablet = !isPhone && !isDesktop;
  const s = useBookingsList();
  const r = useReservationsList();
  const headingRef = useFocusHeadingOnNavigate<HTMLHeadingElement>();
  // The active tab lives in the URL (BON-478) so workshop detail can link
  // straight to the reservations list.
  const [params, setParams] = useSearchParams();
  const tab: BookingTab = params.get('tab') === 'reservations' ? 'reservations' : 'bookings';
  const setTab = (next: BookingTab) => {
    // A copy, not a whole-string replace, so a future filter param survives a
    // tab switch. `replace` keeps tabs out of history.
    const nextParams = new URLSearchParams(params);
    if (next === 'reservations') nextParams.set('tab', next);
    else nextParams.delete('tab');
    setParams(nextParams, { replace: true });
  };
  const { mode: shell, displayName } = useAppMode();
  // The modal itself now lives in the global launcher (BON-142) — this page
  // just triggers it, same as the command palette + "c then b" shortcut. A
  // successful create refreshes this page's list/stats via bookingsRefreshBus
  // (wired inside useBookingsListState), regardless of entry point.
  // `canCreateBooking` is consumed, not just `openCreateBooking`: the launcher
  // is documented as a no-op without permission, so rendering the button
  // unconditionally would give any role that lacks both write:bookings and
  // write:comp-bookings a live-looking control that silently did nothing.
  const { openCreateBooking, canCreateBooking } = useCreateBookingLauncher();

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          {isDesktop && (
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">
              All workshops
            </p>
          )}
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-2xl font-bold uppercase tracking-wide text-moss-900 dark:text-moss-300 focus:outline-none"
          >
            Bookings
          </h1>
        </div>
        {!isPhone && canCreateBooking && <NewBookingButton onClick={openCreateBooking} />}
      </div>

      <Tabs
        active={tab}
        onChange={setTab}
        bookingCount={s.totalCount}
        reservationCount={r.outstandingCount}
      />

      {tab === 'reservations' ? (
        <ReservationsPanel s={r} actorName={displayName} />
      ) : (
        <BookingsTabBody s={s} isDesktop={isDesktop} isTablet={isTablet} shell={shell} />
      )}

      {/* Mobile FAB */}
      {isPhone && canCreateBooking && (
        <div className="fixed bottom-20 right-4 z-10">
          <NewBookingButton fab onClick={openCreateBooking} />
        </div>
      )}
    </div>
  );
}
